import React, { useEffect, useState, useCallback } from "react";
import MUIDataTable, {
  MUIDataTableColumn,
  MUIDataTableOptions,
} from "mui-datatables";
import { createTheme, ThemeProvider, Theme } from "@mui/material/styles";
import {
  Select,
  MenuItem,
  Typography,
  Card,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Visibility } from "@mui/icons-material";
import axios from "../../api";
import { useSelector } from "react-redux";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useNavigate, NavigateFunction } from "react-router-dom";
import { RootState } from "store";

const getMuiTheme = (theme: Theme): Theme =>
  createTheme({
    components: {
      MUIDataTable: {
        styleOverrides: {
          responsiveScroll: {
            maxHeight: "unset",
            overflowX: "auto",
          },
        },
      },
    },
  });

const tableOptions: MUIDataTableOptions = {
  selectableRows: "none",
  selectableRowsHeader: false,
  elevation: 0,
  tableBodyHeight: "auto",
  responsive: "standard",
};

// Extracted component for address cell rendering
const AddressCell: React.FC<{
  value: string;
  cityName: string;
  stateName: string;
}> = ({ value, cityName, stateName }) => {
  const address = `${value}, ${cityName}, ${stateName}`;
  return (
    <Tooltip title={address}>
      <span>
        {address.length > 20 ? `${address.substring(0, 20)}...` : address}
      </span>
    </Tooltip>
  );
};

// Extracted component for actions cell rendering
const ActionsCell: React.FC<{
  ticketId: number;
  onNavigate: NavigateFunction;
}> = ({ ticketId, onNavigate }) => {
  return (
    <IconButton
      aria-label="view"
      onClick={() =>
        onNavigate(`/employee/ticket-view/${ticketId}`, {
          state: { from: "/employee/eventsoverview" },
        })
      }
    >
      <Visibility />
    </IconButton>
  );
};

// Render helper functions - OUTSIDE the component
const renderAddressCell = (
  value: string,
  tableMeta: any,
  navigate: NavigateFunction
) => {
  const data = tableMeta.tableData[tableMeta.rowIndex];
  return (
    <AddressCell
      value={value}
      cityName={data.city_name}
      stateName={data.state_name}
    />
  );
};

const renderActionsCell = (
  value: any,
  tableMeta: any,
  navigate: NavigateFunction
) => {
  const data = tableMeta.tableData[tableMeta.rowIndex];
  return <ActionsCell ticketId={data.ticket_id} onNavigate={navigate} />;
};

const EventsOverview = () => {
  const [eventType, setEventType] = useState("Scheduled");
  const [rows, setRows] = useState([]);
  const user = useSelector((state: RootState) => state.auth.user);
  const navigate = useNavigate();

  const fetchTickets = useCallback(async () => {
    try {
      const response = await axios.get(`/tickets/employee/${user?.userId}`, {
        params: {
          status_id: 2,
        },
      });
      const tickets = response.data?.list || [];

      // Separate tickets based on employee_arrival_date
      const scheduledTickets = tickets.filter(
        (ticket: { employee_arrival_date: Date | string }) =>
          ticket.employee_arrival_date
      );
      const unscheduledTickets = tickets.filter(
        (ticket: { employee_arrival_date: Date | string }) =>
          !ticket.employee_arrival_date
      );

      setRows(
        eventType === "Scheduled" ? scheduledTickets : unscheduledTickets
      );
    } catch (error) {
      console.error("Error fetching tickets:", error);
    }
  }, [user?.userId, eventType]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets, eventType]);

  const columns: MUIDataTableColumn[] = [
    { name: "ticket_service_id", label: "Service ID" },
    { name: "customer_name", label: "Customer" },
    { name: "customer_phone", label: "Phone" },
    {
      name: "address",
      label: "Address",
      options: {
        filter: false,
        customBodyRender: (value, tableMeta) =>
          renderAddressCell(value, tableMeta, navigate),
      },
    },
    { name: "description", label: "Description" },
    { name: "category_name", label: "Category" },
    {
      name: "Actions",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value, tableMeta) =>
          renderActionsCell(value, tableMeta, navigate),
      },
    },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Card
        style={{ padding: "24px", margin: "16px auto", maxWidth: "2000px" }}
      >
        <Typography variant="h4" gutterBottom>
          Events Overview
        </Typography>

        <Select
          value={eventType}
          onChange={(e) => setEventType(e.target.value)}
          style={{ marginBottom: "16px", width: "200px" }}
        >
          <MenuItem value="Scheduled">Scheduled</MenuItem>
          <MenuItem value="Unscheduled">Unscheduled</MenuItem>
        </Select>

        <ThemeProvider theme={getMuiTheme}>
          <MUIDataTable
            title={`${eventType} Tickets`}
            data={rows}
            columns={columns}
            options={tableOptions}
          />
        </ThemeProvider>
      </Card>
    </DashboardLayout>
  );
};

export default EventsOverview;
